import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Grid, useTheme, useMediaQuery, IconButton, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Activity,
  Target,
  RefreshCw,
  CreditCard,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react';
import agtFinService from '../services/agtFinService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// ===========================================
// 🎨 PALETA DE COLORES EN ESCALA DE GRISES
// ===========================================
const getGrayscalePalette = (theme) => ({
  // Colores principales en escala de grises
  primary: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  secondary: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  tertiary: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  quaternary: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  
  // Gradientes para gráficos
  gradients: {
    primary: theme.palette.mode === 'dark' 
      ? 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)'
      : 'linear-gradient(180deg, #000000 0%, #424242 100%)',
    
    secondary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #E0E0E0 0%, #BDBDBD 100%)'
      : 'linear-gradient(180deg, #424242 0%, #616161 100%)',
    
    tertiary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #BDBDBD 0%, #9E9E9E 100%)'
      : 'linear-gradient(180deg, #616161 0%, #757575 100%)',
    
    quaternary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #9E9E9E 0%, #757575 100%)'
      : 'linear-gradient(180deg, #757575 0%, #9E9E9E 100%)'
  },
  
  // Colores para estados (manteniendo la escala de grises)
  success: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  warning: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  error: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  info: theme.palette.mode === 'dark' ? '#757575' : '#9E9E9E',
  
  // Opacidades para diferentes elementos
  backgrounds: {
    light: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    medium: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
    dark: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)'
  }
});

// Componentes estilizados
const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  width: '100%',
  maxWidth: 800,
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

// NUEVO: Selector de mes estilizado
const MonthSelector = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 16,
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : (theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.03)'),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      gap: theme.spacing(1),
    }
  };
});

const MonthNavButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.2)'
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
    color: isWallpaperMode
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.3)'
        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
      transform: 'scale(1.05)',
    },
    '&:disabled': {
      opacity: 0.3,
      transform: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: 36,
      height: 36,
    }
  };
});

const MonthDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: '200px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '150px',
  }
}));

const CurrentMonthText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  color: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  }
}));

const MonthYearSubtext = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 255, 255, 0.8)'
    : (theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161'),
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  }
}));

// NUEVO: Indicador de período
const PeriodIndicator = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: '4px 8px',
    borderRadius: 8,
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.2)'
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
    fontSize: '0.75rem',
    fontWeight: 500,
    color: isWallpaperMode
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242'),
  };
});

// Selector de gráficos estilizado
const ChartSelector = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '40px',
    height: '40px',
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(40, 40, 42, 0.94)'
        : 'rgba(242, 242, 247, 1)'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : 'none',
    padding: '6px',
    borderRadius: '24px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '400px',
    margin: '0 auto',
    marginBottom: theme.spacing(4),
  };
});

// Indicador del selector
const SelectorIndicator = styled('div')(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    height: '28px',
    borderRadius: '24px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.25)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(118, 118, 128, 0.24)'
        : '#FFFFFF'),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 0,
    boxShadow: isWallpaperMode
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? 'none'
        : '0 2px 8px rgba(0, 0, 0, 0.04)')
  };
});

// Botón de selección de gráfico
const ChartSelectorButton = styled('button')(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '28px',
    height: '28px',
    minWidth: '60px',
    padding: '0 12px',
    borderRadius: '24px',
    border: 'none',
    backgroundColor: 'transparent',
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.8)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.7)'
        : '#666666'),
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'none',
    zIndex: 1,
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    '&.selected': {
      color: isWallpaperMode
        ? '#FFFFFF'
        : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
      fontWeight: 600,
    },
    '&:hover:not(.selected)': {
      color: isWallpaperMode
        ? '#FFFFFF'
        : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
      opacity: 0.8,
    },
  };
});

// Contenedor del gráfico
const ChartContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : (theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.03)'),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    }
  };
});

// Componente de carga
const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  minHeight: 200
}));

// Contenedor clickeable para las métricas
const ClickableMetricContainer = styled(Box)(({ theme, selected }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    cursor: 'pointer',
    padding: theme.spacing(1),
    borderRadius: 8,
    transition: 'all 0.2s ease',
    backgroundColor: selected
      ? (isWallpaperMode
        ? 'rgba(255, 255, 255, 0.2)'
        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'))
      : 'transparent',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.15)'
        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
    }
  };
});

// ===========================================
// 📊 COMPONENTES DE GRÁFICOS (sin cambios)
// ===========================================

// Componente de placeholder para gráficos de barras
const BarChartPlaceholder = ({ theme, financialStats, userProfile, colors, t }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!financialStats) return null;

  const maxValue = Math.max(
    financialStats.monthlyIncome.current,
    financialStats.bills.current,
    financialStats.expenses.current,
    financialStats.savings.current
  );

  const calculateHeight = (value) => {
    if (maxValue === 0) return '10%';
    return `${Math.max((value / maxValue) * 100, 10)}%`;
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
        {t('agtfin.dashboard.viewTitles.bars')}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Barra de Ingresos */}
        <Grid item xs={6} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('agtfin.dashboard.headers.incomes')}
            </Typography>
            <Box sx={{ 
              height: 120, 
              display: 'flex', 
              alignItems: 'end', 
              justifyContent: 'center',
              mb: 1
            }}>
              <Box sx={{
                width: 40,
                height: calculateHeight(financialStats.monthlyIncome.current),
                background: colors.gradients.primary,
                borderRadius: '6px 6px 0 0'
              }} />
            </Box>
            <Typography variant="body1" fontWeight={600} sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
              {userProfile?.currency} {financialStats.monthlyIncome.current.toLocaleString()}
            </Typography>
            {financialStats.monthlyIncome.change !== 0 && (
              <Typography variant="caption" color={financialStats.monthlyIncome.changeType === 'increase' ? colors.success : colors.error}>
                {financialStats.monthlyIncome.change > 0 ? '+' : ''}{financialStats.monthlyIncome.change}%
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Barra de Cuentas/Facturas */}
        <Grid item xs={6} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('agtfin.dashboard.headers.bills')}
            </Typography>
            <Box sx={{ 
              height: 120, 
              display: 'flex', 
              alignItems: 'end', 
              justifyContent: 'center',
              mb: 1
            }}>
              <Box sx={{
                width: 40,
                height: calculateHeight(financialStats.bills.current),
                background: colors.gradients.secondary,
                borderRadius: '6px 6px 0 0'
              }} />
            </Box>
            <Typography variant="body1" fontWeight={600} sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
              {userProfile?.currency} {financialStats.bills.current.toLocaleString()}
            </Typography>
            {financialStats.bills.change !== 0 && (
              <Typography variant="caption" color={financialStats.bills.changeType === 'decrease' ? colors.success : colors.error}>
                {financialStats.bills.change > 0 ? '+' : ''}{financialStats.bills.change}%
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              {financialStats.bills.pending || 0} {t('agtfin.dashboard.pending')}
            </Typography>
          </Box>
        </Grid>

        {/* Barra de Gastos */}
        <Grid item xs={6} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('agtfin.dashboard.headers.expenses')}
            </Typography>
            <Box sx={{ 
              height: 120, 
              display: 'flex', 
              alignItems: 'end', 
              justifyContent: 'center',
              mb: 1
            }}>
              <Box sx={{
                width: 40,
                height: calculateHeight(financialStats.expenses.current),
                background: colors.gradients.tertiary,
                borderRadius: '6px 6px 0 0'
              }} />
            </Box>
            <Typography variant="body1" fontWeight={600} sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
              {userProfile?.currency} {financialStats.expenses.current.toLocaleString()}
            </Typography>
            {financialStats.expenses.change !== 0 && (
              <Typography variant="caption" color={financialStats.expenses.changeType === 'decrease' ? colors.success : colors.error}>
                {financialStats.expenses.change > 0 ? '+' : ''}{financialStats.expenses.change}%
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Barra de Ahorros */}
        <Grid item xs={6} sm={6} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('agtfin.dashboard.headers.savings')}
            </Typography>
            <Box sx={{ 
              height: 120, 
              display: 'flex', 
              alignItems: 'end', 
              justifyContent: 'center',
              mb: 1
            }}>
              <Box sx={{
                width: 40,
                height: calculateHeight(financialStats.savings.current),
                background: colors.gradients.quaternary,
                borderRadius: '6px 6px 0 0'
              }} />
            </Box>
            <Typography variant="body1" fontWeight={600} sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
              {userProfile?.currency} {financialStats.savings.current.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {financialStats.savings.progress}% {t('agtfin.savings.ofGoals')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Componente de placeholder para gráfico de torta
const PieChartPlaceholder = ({ theme, financialStats, userProfile, colors, t }) => {
  if (!financialStats) return null;

  const total = financialStats.monthlyIncome.current + financialStats.bills.current + 
                financialStats.expenses.current + financialStats.savings.current;

  const calculateAngle = (value) => {
    if (total === 0) return 0;
    return (value / total) * 360;
  };

  const incomeAngle = calculateAngle(financialStats.monthlyIncome.current);
  const billsAngle = calculateAngle(financialStats.bills.current);
  const expensesAngle = calculateAngle(financialStats.expenses.current);

  const separatorColor = theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF';

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
        {t('agtfin.dashboard.viewTitles.pie')}
      </Typography>
      
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={6}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: 200
          }}>
            <Box sx={{
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: `conic-gradient(
                ${colors.primary} 0deg ${incomeAngle}deg,
                ${separatorColor} ${incomeAngle}deg ${incomeAngle + 2}deg,
                ${colors.secondary} ${incomeAngle + 2}deg ${incomeAngle + billsAngle}deg,
                ${separatorColor} ${incomeAngle + billsAngle}deg ${incomeAngle + billsAngle + 2}deg,
                ${colors.tertiary} ${incomeAngle + billsAngle + 2}deg ${incomeAngle + billsAngle + expensesAngle}deg,
                ${separatorColor} ${incomeAngle + billsAngle + expensesAngle}deg ${incomeAngle + billsAngle + expensesAngle + 2}deg,
                ${colors.quaternary} ${incomeAngle + billsAngle + expensesAngle + 2}deg 360deg
              )`,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
              }
            }} />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: colors.primary
              }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>{t('agtfin.dashboard.headers.incomes')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {userProfile?.currency} {financialStats.monthlyIncome.current.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: colors.secondary
              }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>{t('agtfin.dashboard.headers.bills')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {userProfile?.currency} {financialStats.bills.current.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: colors.tertiary
              }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>{t('agtfin.dashboard.headers.expenses')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {userProfile?.currency} {financialStats.expenses.current.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: colors.quaternary
              }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>{t('agtfin.dashboard.headers.savings')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {userProfile?.currency} {financialStats.savings.current.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Componente de placeholder para gráfico de tendencias
const TrendChartPlaceholder = ({ theme, financialStats, userProfile, colors, t }) => {
  if (!financialStats) return null;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
        {t('agtfin.dashboard.viewTitles.trends')}
      </Typography>
      
      <Box sx={{ height: 200, position: 'relative', mb: 3 }}>
        {/* Líneas de referencia */}
        <Box sx={{ 
          position: 'absolute', 
          width: '100%', 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          {[0, 1, 2, 3, 4].map((line) => (
            <Box key={line} sx={{ 
              height: 1, 
              backgroundColor: colors.backgrounds.light,
              width: '100%'
            }} />
          ))}
        </Box>
        
        {/* Línea de tendencia simulada */}
        <Box sx={{ 
          position: 'absolute',
          top: '50%',
          left: 0,
          width: '100%',
          height: 2,
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.tertiary} 100%)`,
          borderRadius: 1,
          transform: 'translateY(-50%) rotate(-2deg)',
          transformOrigin: 'left center'
        }} />
        
        {/* Puntos de datos */}
        {[20, 35, 60, 80].map((position, index) => (
          <Box key={index} sx={{
            position: 'absolute',
            left: `${position}%`,
            top: `${50 - (index * 5)}%`,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: colors.primary,
            border: '2px solid',
            borderColor: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.3)'
              : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
            transform: 'translate(-50%, -50%)'
          }} />
        ))}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: colors.success }}>
              {financialStats.monthlyIncome.change > 0 ? '+' : ''}{financialStats.monthlyIncome.change}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('agtfin.dashboard.headers.incomes')} {t('agtfin.dashboard.vsLastMonth')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: financialStats.bills.changeType === 'decrease' ? colors.success : colors.error }}>
              {financialStats.bills.change > 0 ? '+' : ''}{financialStats.bills.change}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('agtfin.dashboard.headers.bills')} {t('agtfin.dashboard.vsLastMonth')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: financialStats.expenses.changeType === 'decrease' ? colors.success : colors.error }}>
              {financialStats.expenses.change > 0 ? '+' : ''}{financialStats.expenses.change}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('agtfin.dashboard.headers.expenses')} {t('agtfin.dashboard.vsLastMonth')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: colors.info }}>
              +{financialStats.savings.monthlyContribution || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('agtfin.dashboard.savedThisMonth')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Componente de placeholder para vista de objetivos
const GoalsChartPlaceholder = ({ theme, financialStats, userProfile, colors, t }) => {
  if (!financialStats) return null;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
        {t('agtfin.dashboard.viewTitles.goals')}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Objetivo de Ingresos */}
        <Grid item xs={12} sm={6}>
          <Box sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.backgrounds.light
          }}>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
              {t('agtfin.dashboard.incomeGoal')}
            </Typography>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Box sx={{ 
                height: 8, 
                borderRadius: 4, 
                backgroundColor: colors.backgrounds.medium
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${Math.min((financialStats.monthlyIncome.current / financialStats.monthlyIncome.goal) * 100, 100)}%`,
                  borderRadius: 4,
                  background: colors.gradients.primary
                }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 0, top: -20 }}>
                {Math.round((financialStats.monthlyIncome.current / financialStats.monthlyIncome.goal) * 100)}%
              </Typography>
            </Box>
            <Typography variant="body2">
              {userProfile?.currency} {financialStats.monthlyIncome.current.toLocaleString()} / {financialStats.monthlyIncome.goal.toLocaleString()}
            </Typography>
            </Box>
        </Grid>

        {/* Objetivo de Ahorros */}
        <Grid item xs={12} sm={6}>
          <Box sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.backgrounds.light
          }}>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
              {t('agtfin.dashboard.savingsGoal')}
            </Typography>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Box sx={{ 
                height: 8, 
                borderRadius: 4, 
                backgroundColor: colors.backgrounds.medium
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${financialStats.savings.progress}%`,
                  borderRadius: 4,
                  background: colors.gradients.quaternary
                }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 0, top: -20 }}>
                {financialStats.savings.progress}%
              </Typography>
            </Box>
            <Typography variant="body2">
              {userProfile?.currency} {financialStats.savings.current.toLocaleString()} / {financialStats.savings.totalTargets.toLocaleString()}
            </Typography>
          </Box>
        </Grid>

        {/* Presupuesto de Cuentas */}
        <Grid item xs={12} sm={6}>
          <Box sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.backgrounds.light
          }}>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
              {t('agtfin.dashboard.billsBudget')}
            </Typography>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Box sx={{ 
                height: 8, 
                borderRadius: 4, 
                backgroundColor: colors.backgrounds.medium
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${Math.min((financialStats.bills.current / financialStats.bills.budget) * 100, 100)}%`,
                  borderRadius: 4,
                  background: colors.gradients.secondary
                }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 0, top: -20 }}>
                {Math.round((financialStats.bills.current / financialStats.bills.budget) * 100)}%
              </Typography>
            </Box>
            <Typography variant="body2">
              {userProfile?.currency} {financialStats.bills.current.toLocaleString()} / {financialStats.bills.budget.toLocaleString()}
            </Typography>
          </Box>
        </Grid>

        {/* Presupuesto de Gastos */}
        <Grid item xs={12} sm={6}>
          <Box sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.backgrounds.light
          }}>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
              {t('agtfin.dashboard.expensesBudget')}
            </Typography>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Box sx={{ 
                height: 8, 
                borderRadius: 4, 
                backgroundColor: colors.backgrounds.medium
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${Math.min((financialStats.expenses.current / financialStats.expenses.budget) * 100, 100)}%`,
                  borderRadius: 4,
                  background: colors.gradients.tertiary
                }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 0, top: -20 }}>
                {Math.round((financialStats.expenses.current / financialStats.expenses.budget) * 100)}%
              </Typography>
            </Box>
            <Typography variant="body2">
              {userProfile?.currency} {financialStats.expenses.current.toLocaleString()} / {financialStats.expenses.budget.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ===========================================
// 🗓️ UTILIDADES DE FECHA PARA EL SELECTOR
// ===========================================

// Ahora usamos traducciones en lugar de array hardcodeado
// const MONTHS_ES = [
//   'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
//   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
// ];

const getCurrentMonthYear = (t) => {
  const now = new Date();
  const monthKeys = [
    'agtfin.months.january', 'agtfin.months.february', 'agtfin.months.march',
    'agtfin.months.april', 'agtfin.months.may', 'agtfin.months.june',
    'agtfin.months.july', 'agtfin.months.august', 'agtfin.months.september',
    'agtfin.months.october', 'agtfin.months.november', 'agtfin.months.december'
  ];
  return {
    month: now.getMonth(),
    year: now.getFullYear(),
    monthName: t(monthKeys[now.getMonth()])
  };
};

const getMonthYearString = (month, year, t) => {
  const monthKeys = [
    'agtfin.months.january', 'agtfin.months.february', 'agtfin.months.march',
    'agtfin.months.april', 'agtfin.months.may', 'agtfin.months.june',
    'agtfin.months.july', 'agtfin.months.august', 'agtfin.months.september',
    'agtfin.months.october', 'agtfin.months.november', 'agtfin.months.december'
  ];
  return `${t(monthKeys[month])} ${year}`;
};

const isCurrentMonth = (month, year) => {
  const now = new Date();
  return month === now.getMonth() && year === now.getFullYear();
};

const isFutureMonth = (month, year) => {
  const now = new Date();
  const selectedDate = new Date(year, month, 1);
  const currentDate = new Date(now.getFullYear(), now.getMonth(), 1);
  return selectedDate > currentDate;
};

// ===========================================
// 📊 COMPONENTE PRINCIPAL CON SELECTOR DE MES
// ===========================================

const FinDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  // Estados del componente
  const [selectedChart, setSelectedChart] = useState(0);
  const [financialStats, setFinancialStats] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  
  // NUEVOS ESTADOS PARA EL SELECTOR DE MES
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const current = getCurrentMonthYear(t);
    return { month: current.month, year: current.year };
  });
  const [loadingMonth, setLoadingMonth] = useState(false);
  
  const colors = getGrayscalePalette(theme);
  
  // Referencias para el selector
  const selectorRef = useRef(null);
  const buttonRef0 = useRef(null);
  const buttonRef1 = useRef(null);
  const buttonRef2 = useRef(null);
  const buttonRef3 = useRef(null);
  const buttonRefs = useMemo(() => [buttonRef0, buttonRef1, buttonRef2, buttonRef3], []);
  
  // Estado para el indicador del selector
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: '6px',
    width: '60px'
  });

  // Definición de tipos de gráficos
  const chartTypes = [
    { id: 0, label: t('agtfin.dashboard.chartTypes.bars'), icon: BarChart3 },
    { id: 1, label: t('agtfin.dashboard.chartTypes.pie'), icon: PieChart },
    { id: 2, label: t('agtfin.dashboard.chartTypes.trends'), icon: TrendingUp },
    { id: 3, label: t('agtfin.dashboard.chartTypes.goals'), icon: Target }
  ];

  // Definiciones de explicaciones para las métricas
  const getMetricExplanations = (t) => ({
    balance: {
      title: t('agtfin.dashboard.metricExplanations.balance.title'),
      description: t('agtfin.dashboard.metricExplanations.balance.description')
    },
    ahorro: {
      title: t('agtfin.dashboard.metricExplanations.savings.title'),
      description: t('agtfin.dashboard.metricExplanations.savings.description')
    },
    egresos: {
      title: t('agtfin.dashboard.metricExplanations.expenses.title'),
      description: t('agtfin.dashboard.metricExplanations.expenses.description')
    },
    eficiencia: {
      title: t('agtfin.dashboard.metricExplanations.efficiency.title'),
      description: t('agtfin.dashboard.metricExplanations.efficiency.description')
    }
  });

  // NUEVA FUNCIÓN: Cargar datos del dashboard para un mes específico
  const loadDashboardData = async (month = null, year = null) => {
    try {
      setLoadingMonth(true);
      setError(null);
      
      const targetMonth = month !== null ? month : selectedMonth.month;
      const targetYear = year !== null ? year : selectedMonth.year;

      // const { startDate, endDate } = getMonthRange(targetMonth, targetYear);

      console.log(`📊 Cargando datos del dashboard para ${getMonthYearString(targetMonth, targetYear, t)}...`);

      // Por ahora usamos el método existente, pero deberías modificar el servicio para aceptar parámetros de fecha
      const data = await agtFinService.getFinDashboardData();

      // TODO: Cuando actualices el servicio, la llamada debería ser:
      // const { startDate, endDate } = getMonthRange(targetMonth, targetYear);
      // const data = await agtFinService.getFinDashboardData({ startDate, endDate });
      
      setFinancialStats(data.financialStats);
      setUserProfile(data.userProfile);
      setLastUpdated(new Date());
      
      console.log(`✅ Datos del dashboard cargados exitosamente para ${getMonthYearString(targetMonth, targetYear, t)}:`, data);
    } catch (err) {
      console.error('❌ Error cargando datos del dashboard:', err);
      setError(err.message || 'Error al cargar los datos del dashboard');
    } finally {
      setLoadingMonth(false);
      setLoading(false);
    }
  };

  // NUEVA FUNCIÓN: Cambiar mes
  const handleMonthChange = async (direction) => {
    const newMonth = direction === 'prev' 
      ? (selectedMonth.month === 0 ? 11 : selectedMonth.month - 1)
      : (selectedMonth.month === 11 ? 0 : selectedMonth.month + 1);
    
    const newYear = direction === 'prev' 
      ? (selectedMonth.month === 0 ? selectedMonth.year - 1 : selectedMonth.year)
      : (selectedMonth.month === 11 ? selectedMonth.year + 1 : selectedMonth.year);
    
    // Prevenir navegar a meses futuros
    if (isFutureMonth(newMonth, newYear)) {
      return;
    }
    
    setSelectedMonth({ month: newMonth, year: newYear });
    await loadDashboardData(newMonth, newYear);
  };

  // NUEVA FUNCIÓN: Ir al mes actual
  const goToCurrentMonth = async () => {
    const current = getCurrentMonthYear(t);
    setSelectedMonth({ month: current.month, year: current.year });
    await loadDashboardData(current.month, current.year);
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Actualizar indicador cuando cambia el gráfico seleccionado
  useEffect(() => {
    const updateIndicator = () => {
      if (buttonRefs[selectedChart]?.current && selectorRef.current) {
        const buttonElement = buttonRefs[selectedChart].current;
        const selectorContainer = selectorRef.current;

        if (buttonElement && selectorContainer) {
          const containerRect = selectorContainer.getBoundingClientRect();
          const buttonRect = buttonElement.getBoundingClientRect();
          const left = buttonRect.left - containerRect.left;

          setIndicatorStyle({
            left: `${left}px`,
            width: `${buttonRect.width}px`
          });
        }
      }
    };

    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [selectedChart, buttonRefs]);

  // Función para refrescar datos
  const handleRefresh = () => {
    loadDashboardData();
  };

  // Función para manejar clicks en las métricas
  const handleMetricClick = (metricKey) => {
    setSelectedMetric(selectedMetric === metricKey ? null : metricKey);
  };

  // Determinar si se pueden navegar los meses
  const canGoPrev = () => {
    // Permitir navegar hasta 12 meses atrás
    const minDate = new Date();
    minDate.setMonth(minDate.getMonth() - 12);
    const selectedDate = new Date(selectedMonth.year, selectedMonth.month);
    return selectedDate > minDate;
  };

  const canGoNext = () => {
    return !isFutureMonth(
      selectedMonth.month === 11 ? 0 : selectedMonth.month + 1,
      selectedMonth.month === 11 ? selectedMonth.year + 1 : selectedMonth.year
    );
  };

  // Renderizar el gráfico seleccionado
  const renderChart = () => {
    if (loading || loadingMonth) {
      return (
        <LoadingContainer>
          <CircularProgress size={40} sx={{ color: colors.primary, mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {loadingMonth ? t('agtfin.dashboard.loading.month') : t('agtfin.dashboard.loading.financialData')}
          </Typography>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <IconButton onClick={handleRefresh} sx={{ color: colors.primary }}>
            <RefreshCw size={20} />
          </IconButton>
        </Box>
      );
    }

    if (!financialStats) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="info">
            {t('agtfin.dashboard.noDataAvailable')}
          </Alert>
        </Box>
      );
    }

    switch (selectedChart) {
      case 0:
        return <BarChartPlaceholder
          theme={theme}
          financialStats={financialStats}
          userProfile={userProfile}
          colors={colors}
          t={t}
        />;
      case 1:
        return <PieChartPlaceholder
          theme={theme}
          financialStats={financialStats}
          userProfile={userProfile}
          colors={colors}
          t={t}
        />;
      case 2:
        return <TrendChartPlaceholder
          theme={theme}
          financialStats={financialStats}
          userProfile={userProfile}
          colors={colors}
          t={t}
        />;
      case 3:
        return <GoalsChartPlaceholder
          theme={theme}
          financialStats={financialStats}
          userProfile={userProfile}
          colors={colors}
          t={t}
        />;
      default:
        return null;
    }
  };

  return (
    <DashboardContainer
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header con título y botón de refresh */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          {t('agtfin.dashboard.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              {t('agtfin.dashboard.updated')} {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <IconButton 
            onClick={handleRefresh} 
            disabled={loading || loadingMonth}
            sx={{ color: colors.primary }}
            size="small"
          >
            <RefreshCw size={16} />
          </IconButton>
        </Box>
      </Box>

      {/* NUEVO: Selector de mes */}
      <MonthSelector
        component={motion.div}
        variants={itemVariants}
      >
        <MonthNavButton 
          onClick={() => handleMonthChange('prev')}
          disabled={!canGoPrev() || loadingMonth}
        >
          <ChevronLeft size={20} />
        </MonthNavButton>
        
        <MonthDisplay>
          <CurrentMonthText>
            {getMonthYearString(selectedMonth.month, selectedMonth.year, t)}
          </CurrentMonthText>
          <MonthYearSubtext component="div">
            <Calendar size={14} />
            {isCurrentMonth(selectedMonth.month, selectedMonth.year) ? (
              <PeriodIndicator>
                {t('agtfin.dashboard.currentMonth')}
              </PeriodIndicator>
            ) : (
              `${Math.abs(
                (new Date().getFullYear() - selectedMonth.year) * 12 +
                (new Date().getMonth() - selectedMonth.month)
              )} ${Math.abs(
                (new Date().getFullYear() - selectedMonth.year) * 12 +
                (new Date().getMonth() - selectedMonth.month)
              ) === 1 ? t('agtfin.dashboard.month') : t('agtfin.dashboard.month') + 'es'} ${
                selectedMonth.year < new Date().getFullYear() ||
                (selectedMonth.year === new Date().getFullYear() && selectedMonth.month < new Date().getMonth())
                ? t('agtfin.dashboard.monthsAgo') : t('agtfin.dashboard.monthsAhead')
              }`
            )}
          </MonthYearSubtext>
        </MonthDisplay>
        
        <MonthNavButton 
          onClick={() => handleMonthChange('next')}
          disabled={!canGoNext() || loadingMonth}
        >
          <ChevronRight size={20} />
        </MonthNavButton>
      </MonthSelector>

      {/* Botón para volver al mes actual (solo si no estamos en el mes actual) */}
      <AnimatePresence mode="wait">
        {!isCurrentMonth(selectedMonth.month, selectedMonth.year) && (
          <Box
            component={motion.div}
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
          >
            <Box
              component={motion.div}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={goToCurrentMonth}
              sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              padding: '6px 12px',
              borderRadius: 2,
              backgroundColor: colors.backgrounds.light,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: colors.backgrounds.medium,
                transform: 'scale(1.02)',
              },
            }}
          >
            <Activity size={14} />
            <Typography variant="caption" fontWeight={500}>
              {t('agtfin.dashboard.goToCurrentMonth')}
            </Typography>
          </Box>
        </Box>
        )}
      </AnimatePresence>

      {/* Selector de tipo de gráfico */}
      <ChartSelector
        component={motion.div}
        variants={itemVariants}
        ref={selectorRef}
      >
        <SelectorIndicator style={indicatorStyle} />
        
        {chartTypes.map((chartType, index) => {
          const IconComponent = chartType.icon;
          return (
            <ChartSelectorButton
              key={chartType.id}
              ref={buttonRefs[index]}
              onClick={() => setSelectedChart(chartType.id)}
              className={selectedChart === chartType.id ? 'selected' : ''}
            >
              <IconComponent size={14} />
              {!isMobile && chartType.label}
            </ChartSelectorButton>
          );
        })}
      </ChartSelector>

      {/* Contenedor del gráfico */}
      <ChartContainer
        component={motion.div}
        variants={itemVariants}
      >
        {renderChart()}
      </ChartContainer>

      {/* Resumen rápido */}
      {financialStats && (
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? 'rgba(128, 128, 128, 0.25)'
            : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
          backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          border: theme.palette.actualMode === 'wallpaper'
            ? '1px solid rgba(255, 255, 255, 0.2)'
            : (theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.03)')
        }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            {t('agtfin.dashboard.summary', { month: getMonthYearString(selectedMonth.month, selectedMonth.year, t) })}
          </Typography>
          
          <Grid container spacing={2} sx={{ textAlign: 'center' }}>
            {/* Balance neto */}
            <Grid item xs={6} sm={3}>
              <ClickableMetricContainer
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                selected={selectedMetric === 'balance'}
                onClick={() => handleMetricClick('balance')}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {t('agtfin.dashboard.metrics.balance')}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: colors.primary }}>
                  {userProfile?.currency} {financialStats.summary.netIncome.toLocaleString()}
                </Typography>
              </ClickableMetricContainer>
            </Grid>

            {/* Tasa de ahorro */}
            <Grid item xs={6} sm={3}>
              <ClickableMetricContainer
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                selected={selectedMetric === 'ahorro'}
                onClick={() => handleMetricClick('ahorro')}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {t('agtfin.dashboard.metrics.savings')}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: colors.primary }}>
                  {financialStats.summary.savingsRate}%
                </Typography>
              </ClickableMetricContainer>
            </Grid>

            {/* Porcentaje de egresos */}
            <Grid item xs={6} sm={3}>
              <ClickableMetricContainer
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                selected={selectedMetric === 'egresos'}
                onClick={() => handleMetricClick('egresos')}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {t('agtfin.dashboard.metrics.expenses')}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: colors.primary }}>
                  {financialStats.summary.egresosRate}%
                </Typography>
              </ClickableMetricContainer>
            </Grid>

            {/* Eficiencia presupuestaria */}
            <Grid item xs={6} sm={3}>
              <ClickableMetricContainer
                component={motion.div}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                selected={selectedMetric === 'eficiencia'}
                onClick={() => handleMetricClick('eficiencia')}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {t('agtfin.dashboard.metrics.efficiency')}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: colors.primary }}>
                  {financialStats.summary.budgetStatus?.overall?.efficiency || 0}%
                </Typography>
              </ClickableMetricContainer>
            </Grid>
          </Grid>

          {/* Explicación dinámica */}
          <Box sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            textAlign: 'center'
          }}>
            {selectedMetric ? (
              <Typography
                component="div"
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                  fontStyle: 'normal',
                  '& .bold': {
                    fontWeight: 'bold',
                    fontSize: isMobile ? '0.7rem' : '0.75rem'
                  }
                }}
              >
                <span className="bold">{getMetricExplanations(t)[selectedMetric].title}:</span> {getMetricExplanations(t)[selectedMetric].description}
              </Typography>
            ) : (
              <Typography
                component="div"
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                  fontStyle: 'normal'
                }}
              >
                {t('agtfin.dashboard.tapMetric')}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Breakdown de Egresos */}
      {financialStats && financialStats.summary.egresosBreakdown && (
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? 'rgba(128, 128, 128, 0.25)'
            : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
          backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          border: theme.palette.actualMode === 'wallpaper'
            ? '1px solid rgba(255, 255, 255, 0.2)'
            : (theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.03)')
        }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            {t('agtfin.dashboard.expenseBreakdown', {
              month: getMonthYearString(selectedMonth.month, selectedMonth.year, t).split(' ')[0]
            })}
          </Typography>
          
          <Grid container spacing={2}>
            {/* Cuentas */}
            <Grid item xs={6} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <CreditCard size={16} color={colors.secondary} />
                  <Typography variant="caption" color="text.secondary">{t('agtfin.dashboard.headers.bills')}</Typography>
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: colors.primary, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                  {financialStats.summary.egresosBreakdown.bills.percentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                  {userProfile?.currency} {financialStats.summary.egresosBreakdown.bills.amount.toLocaleString()}
                </Typography>
              </Box>
            </Grid>

            {/* Gastos */}
            <Grid item xs={6} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <ShoppingCart size={16} color={colors.tertiary} />
                  <Typography variant="caption" color="text.secondary">{t('agtfin.dashboard.headers.expenses')}</Typography>
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: colors.primary, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                  {financialStats.summary.egresosBreakdown.expenses.percentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                  {userProfile?.currency} {financialStats.summary.egresosBreakdown.expenses.amount.toLocaleString()}
                </Typography>
              </Box>
            </Grid>

            {/* Ahorros */}
            <Grid item xs={12} sm={4} sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Box sx={{ textAlign: 'center', width: { xs: '50%', sm: '100%' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <Target size={16} color={colors.quaternary} />
                  <Typography variant="caption" color="text.secondary">{t('agtfin.dashboard.headers.savings')}</Typography>
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: colors.primary, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                  {financialStats.summary.egresosBreakdown.savings.percentage}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                  {userProfile?.currency} {financialStats.summary.egresosBreakdown.savings.amount.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Explicación clara del plan ideal */}
          <Box sx={{ 
            mt: 2, 
            pt: 2, 
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            textAlign: 'center'
          }}>
            <Typography
              component="div"
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                fontStyle: 'normal',
                '& .bold': {
                  fontWeight: 'bold',
                  fontSize: isMobile ? '0.7rem' : '0.75rem'
                }
              }}
            >
              {t('agtfin.dashboard.idealPlan')}
            </Typography>
          </Box>
        </Box>
      )}
    </DashboardContainer>
  );
};

export default FinDashboard;